/**
 * Image processing utilities for template save operations
 * Extracted from AddCanvasTemplate.js to reduce file size
 */
import { compressImage, uploadToS3 } from "./imageUploadUtils";
import { convertBlobToBase64 } from "../../SectionElements/plugins/utils";

const isOffCanvasImage = (img = {}) =>
  img.isOffCanvas === true ||
  img.is_off_canvas === true ||
  img.isOffcanvas === true;

/**
 * Process a single image for saving (uploads to S3 if needed)
 * @param {Object} img - Image object
 * @returns {Promise<Object|null>} Processed image data with imageData and shapeData, or null if skipped
 */
export const processImageForSave = async (img) => {
  try {
    const imageIsOffCanvas = isOffCanvasImage(img);
    // Skip if image already has S3 URL
    const existingSrc = img.src || img.image_path;
    if (
      existingSrc &&
      existingSrc.startsWith("http") &&
      !existingSrc.includes("blob")
    ) {
      // Image already uploaded to S3, use existing URL
      // Clean up img object to prevent nested duplication
      const {
        image_properties,
        shape_info,
        image_id,
        image_path: img_path,
        shape_properties,
        canvas_page_location_and_size,
        shapeData: imgShapeData,
        imageData: imgData,
        compressedImageDataUrl, // Don't send compressed data URL - compress on render instead
        ...cleanImg
      } = img;

      const resolvedIsOnTopLayer =
        cleanImg.isOnTopLayer ??
        img.isOnTopLayer ??
        img.image_properties?.isOnTopLayer ??
        img.shape_info?.isOnTopLayer ??
        false;

      let data = {
        image_id: img.id,
        image_path: existingSrc,
        image_properties: {
          id: img.id,
          x: img.x,
          y: img.y,
          width: img.width,
          height: img.height,
          scaleX: img.scaleX || 1,
          scaleY: img.scaleY || 1,
          rotation: img.rotation || 0,
          name: img.name || "Picture 1",
          ...cleanImg,
          imageDataUrl: existingSrc,
          src: existingSrc,
          originalImageDataUrl: existingSrc,
          uncompressedCroppedImageDataUrl: existingSrc,
          compressedImageDataUrl: existingSrc,
          // Flip transform data
          flipHorizontal: img.flipHorizontal || false,
          flipVertical: img.flipVertical || false,
          hasBakedTransforms: img.hasBakedTransforms || false,
          originalCropRotation: img.originalCropRotation || 0,
          originalWidth: img.originalWidth,
          originalHeight: img.originalHeight,
          referenceUrl:
            img.referenceUrl || img.image_properties?.referenceUrl || "",
          isOnTopLayer: resolvedIsOnTopLayer,
          ...(imageIsOffCanvas ? { isOffcanvas: true } : {}),
        },
      };

      if (data.image_id.includes("marker")) {
        delete data.image_id;
      }

      let dataForShape = {
        height: parseInt(img.height) || 0,
        width: parseInt(img.width) || 0,
        shape_fill: 0,
        shape_color: 0,
        stroke_color: 0,
        stroke_size: 0,
        shape_info: {
          ...cleanImg,
          src: existingSrc,
          imageDataUrl: existingSrc,
          originalImageDataUrl: existingSrc,
          uncompressedCroppedImageDataUrl: existingSrc,
          compressedImageDataUrl: existingSrc,
          originalWidth: img.originalWidth,
          originalHeight: img.originalHeight,
          isOnTopLayer: resolvedIsOnTopLayer,
          ...(imageIsOffCanvas ? { isOffcanvas: true } : {}),
        },
        shape_id: img.id,
        type: "Image",
      };

      if (dataForShape.shape_id.includes("marker")) {
        delete dataForShape.shape_id;
      }

      return {
        imageData: data,
        shapeData: dataForShape,
      };
    }

    // CRITICAL: Always use original image data for S3 upload, never compressed
    // Priority: originalImageDataUrl > imgDataUrl > src (but only if src is not compressed) > image_path
    let src =
      img.originalImageDataUrl ||
      img.imgDataUrl ||
      (!img.compress ? img.src : null) ||
      img.image_path;

    if (src && src.startsWith("blob:")) {
      const base64 = await convertBlobToBase64(src);
      if (base64) src = base64;
    }

    // Validate that we have valid base64 data
    if (!src || typeof src !== "string") {
      console.warn("Invalid image data, skipping:", img.id);
      return null;
    }

    // Check if it's actually base64 data or blob
    if (!src.startsWith("data:image/") && !src.startsWith("blob:")) {
      console.warn("Not a valid image format, skipping:", img.id);
      return null;
    }

    // Generate unique filename for S3 upload
    const timestamp = new Date().getTime();
    const fileName = `canvas-template-image-${img.id}-${timestamp}.png`;
    const fileType = "image/png";

    let finalImageSrc;
    try {
      // Upload to S3 and get permanent URL
      const s3Url = await uploadToS3(src, fileName, fileType);
      finalImageSrc = s3Url;
    } catch (error) {
      console.error(
        "S3 upload failed, falling back to compressed base64:",
        error
      );
      // Fallback to compressed base64 if S3 upload fails
      finalImageSrc = await compressImage(src);
    }

    // Clean up img object to prevent nested duplication
    const {
      image_properties,
      shape_info,
      image_id,
      image_path: img_path,
      shape_properties,
      canvas_page_location_and_size,
      shapeData: imgShapeData,
      imageData: imgData,
      compressedImageDataUrl, // Don't send compressed data URL - compress on render instead
      ...cleanImg
    } = img;

    const resolvedIsOnTopLayer =
      cleanImg.isOnTopLayer ??
      img.isOnTopLayer ??
      img.image_properties?.isOnTopLayer ??
      img.shape_info?.isOnTopLayer ??
      false;

    let data = {
      image_id: img.id,
      image_path: finalImageSrc,
      image_properties: {
        id: img.id,
        x: img.x,
        y: img.y,
        width: img.width,
        height: img.height,
        scaleX: img.scaleX || 1,
        scaleY: img.scaleY || 1,
        rotation: img.rotation || 0,
        name: img.name || "Picture 1",
        ...cleanImg,
        imageDataUrl: finalImageSrc,
        src: finalImageSrc,
        originalImageDataUrl: finalImageSrc,
        uncompressedCroppedImageDataUrl: finalImageSrc,
        compressedImageDataUrl: finalImageSrc,
        // Flip transform data
        flipHorizontal: img.flipHorizontal || false,
        flipVertical: img.flipVertical || false,
        hasBakedTransforms: img.hasBakedTransforms || false,
        originalCropRotation: img.originalCropRotation || 0,
        originalWidth: img.originalWidth,
        originalHeight: img.originalHeight,
        referenceUrl:
          img.referenceUrl || img.image_properties?.referenceUrl || "",
        isOnTopLayer: resolvedIsOnTopLayer,
        ...(imageIsOffCanvas ? { isOffcanvas: true } : {}),
      },
    };

    if (data.image_id.includes("marker")) {
      delete data.image_id;
    }

    let dataForShape = {
      height: parseInt(img.height) || 0,
      width: parseInt(img.width) || 0,
      shape_fill: 0,
      shape_color: 0,
      stroke_color: 0,
      stroke_size: 0,
      shape_info: {
        ...cleanImg,
        src: finalImageSrc,
        originalImageDataUrl: finalImageSrc,
        uncompressedCroppedImageDataUrl: finalImageSrc,
        compressedImageDataUrl: finalImageSrc,
        imageDataUrl: finalImageSrc,
        originalWidth: img.originalWidth,
        originalHeight: img.originalHeight,
        isOnTopLayer: resolvedIsOnTopLayer,
        ...(imageIsOffCanvas ? { isOffcanvas: true } : {}),
      },
      shape_id: img.id,
      type: "Image",
    };

    if (dataForShape.shape_id.includes("marker")) {
      delete dataForShape.shape_id;
    }

    return {
      imageData: data,
      shapeData: dataForShape,
    };
  } catch (error) {
    console.error("Error processing image:", error);
    return null;
  }
};
